import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { getReportingEditReportUrl } from '@futrli/shared-services';
import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';
import { useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard/shared/useDialogWizardState';

import { DashboardDuplicatedWizardScreen } from './screens/dashboard-duplicated-wizard-screen';
import { DashboardDuplicatingWizardScreen } from './screens/dashboard-duplicating-wizard-screen';
import { DuplicateDashboardFormWizardScreen } from './screens/duplicate-dashboard-form-wizard-screen';
import { useDuplicateDashboardContext } from '../context/duplicate-dashboard-context';

interface DuplicateDashboardContainerProps {
  onClose: () => void;
}

export const DuplicateDashboardContainer = ({ onClose }: DuplicateDashboardContainerProps) => {
  const navigate = useNavigate();
  const { form, organisationId, view, workspaceId } = useDuplicateDashboardContext();
  const { dashboard, isLoading, isDuplicating, errorDuplicating, newDashboardId } = view.state;

  const handleSubmit = useCallback(() => {
    if (!form.validate()) {
      return;
    }

    const requestData = form.getRequestData();
    if (requestData) {
      view.duplicateDashboard(requestData);
    }
  }, [form, view]);

  const handleView = useCallback(() => {
    if (dashboard && newDashboardId) {
      navigate(getReportingEditReportUrl(workspaceId, organisationId, newDashboardId));
      onClose();
    }
  }, [dashboard, navigate, newDashboardId, onClose, organisationId, workspaceId]);

  const { screen, handleAction } = useDialogWizardState({
    isLoading: isLoading || isDuplicating,
    error: errorDuplicating,
    onAction: handleSubmit,
    onClose
  });

  switch (screen) {
    case 'loading': {
      return isLoading ? <LoadingWizardScreen /> : <DashboardDuplicatingWizardScreen />;
    }
    case 'success': {
      return <DashboardDuplicatedWizardScreen onClose={onClose} onView={handleView} />;
    }
    default: {
      return (
        <DuplicateDashboardFormWizardScreen
          onClose={onClose}
          onDuplicate={handleAction}
          error={errorDuplicating?.message}
        />
      );
    }
  }
};
